import { z } from "zod";
import { AnswerFinderSuggestionSchema } from "./answer-finder-contracts.ts";
import { campaignGoalKindSchema } from "./campaign-config.ts";
import { isConditionalNoStep } from "./conditional-no.ts";
import {
  BOOKING_CONTEXT_KEY,
  canonical,
  contextValueSchemas,
  isContextKey,
  isExplicitBookingAbsence,
} from "./context-contracts.ts";
import {
  type AuthorizedFollowUpPromise,
  PlanFollowUpPromiseSchema,
  RepQuestionResponseSchema,
} from "./follow-up-promise.ts";
import { leadLinkStepPlacement } from "./lead-scheduling-link.ts";
import { isMaterialStep } from "./materials.ts";
import { outsideGoalStepKind } from "./outside-goal.ts";

// Stable host and provider identifiers include UUID hyphens, context-key underscores and
// provider-style colon prefixes. They are always encoded before entering a URL.
const id = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,199}$/);
const revision = z.number().int().nonnegative();
const text = z.string().min(1).max(100_000);
const instant = z.iso.datetime({ offset: true });
const address = z.email();
const source = z.strictObject({ messageId: id, quote: text.nullable() });
const question = z.strictObject({
  key: id,
  prompt: text,
  uploadAllowed: z.boolean(),
  speechActId: text.nullable().default(null),
  answerMode: z.enum(["text", "yes_no_check"]).default("text"),
  answerFinderSuggestion: AnswerFinderSuggestionSchema.nullable().optional(),
});

export const StepInputSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("reply"), draftRevision: revision }),
  z.strictObject({ kind: z.literal("question"), questions: z.array(question).min(1) }),
  z.strictObject({
    kind: z.literal("connector"),
    action: z.discriminatedUnion("type", [
      z
        .strictObject({
          type: z.literal("meeting_create"),
          bindingId: id,
          organizerId: id,
          attendees: z.array(address).min(1).max(20),
          start: instant,
          end: instant,
          recipientTimezone: z.string().refine((value) => {
            try {
              new Intl.DateTimeFormat("en", { timeZone: value });
              return value.includes("/") || value === "UTC";
            } catch {
              return false;
            }
          }, "An explicit IANA timezone is required"),
          title: text,
          description: text,
          invite: z.literal(true),
        })
        .refine(
          (value) => Date.parse(value.end) > Date.parse(value.start),
          "Meeting must end after it starts",
        ),
      z.strictObject({ type: z.literal("unsubscribe"), bindingId: id, replyId: id }),
      z.strictObject({ type: z.literal("block"), bindingId: id, replyId: id, address }),
    ]),
  }),
  z.strictObject({ kind: z.literal("manual"), instructions: text }),
  z.strictObject({ kind: z.literal("wait"), until: instant, reason: text }),
  z.strictObject({ kind: z.literal("handoff"), instructions: text, recipient: text }),
  /**
   * A conversation the pilot cannot close itself, written down for the person who can.
   *
   * Distinct from `handoff`, which is the developer-escalation step and addresses an operator by a
   * fixed label. This one names a real person taken from the campaign's approved, tenant-scoped
   * `handoff_contact`, and it is never produced without one: an inferred recipient — from a
   * signature, a reply address or a campaign name — is the failure this exists to prevent.
   *
   * `completion` is a literal rather than a flag. The artifact is a recommendation and a record;
   * nothing here delivers it, and no receipt is claimed. The rep marks it complete by hand, and the
   * revision that carries it is the step's own, so an approval bound to an earlier revision does not
   * carry to a later one.
   */
  z.strictObject({
    kind: z.literal("handoff_artifact"),
    responsible: z.strictObject({ contactId: id, name: text }),
    reason: text,
    summary: text,
    completion: z.literal("manual"),
  }),
]);
type StepInput = z.infer<typeof StepInputSchema>;

const ApprovalSchema = z.strictObject({
  actorId: id,
  at: instant,
  stepRevision: revision,
  conversationRevision: revision,
  configRevision: revision,
  draftRevision: revision.nullable(),
});

const StepSchema = z.strictObject({
  id,
  title: text,
  actor: z.enum(["rep", "connector", "system"]),
  revision,
  dependencies: z.array(id),
  mandatory: z.boolean(),
  sources: z.array(source),
  input: StepInputSchema,
  state: z.enum([
    "pending",
    "approved",
    "queued",
    "executing",
    "uncertain",
    "completed",
    "reported",
    "skipped",
    "invalidated",
  ]),
  approval: ApprovalSchema.nullable(),
  operationId: id.nullable(),
});
export type PlanStep = z.infer<typeof StepSchema>;

const evidence = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("rep_claim"), actorId: id, note: text }),
  z.strictObject({
    kind: z.literal("answer"),
    actorId: id,
    values: z.record(id, z.json()),
    uploadIds: z.array(id),
  }),
  z.strictObject({
    kind: z.literal("provider_receipt"),
    operationId: id,
    providerId: id,
    observedAt: instant,
  }),
  z.strictObject({ kind: z.literal("clock"), observedAt: instant }),
]);
export type StepEvidence = z.infer<typeof evidence>;
const operatorOutcome = z.strictObject({
  decisionId: id,
  kind: z.enum(["no_response", "deck_only"]),
  actorId: id,
  reviewedAt: instant,
  conversationRevision: revision,
  configRevision: revision,
});
export type OperatorOutcome = z.infer<typeof operatorOutcome>;
const operatorOutcomeDecision = z.strictObject({
  decisionId: id,
  kind: z.enum(["no_response", "deck_only", "active"]),
  actorId: id,
  reviewedAt: instant,
  conversationRevision: revision,
  configRevision: revision,
});
/**
 * A draft revision the rep reported as sent by hand through the provider. Bound to the exact
 * approved revision rather than to the conversation, so a later inbound reply still opens new work
 * while the revision already in the lead's mailbox stays closed. The app never sends, so this is a
 * rep's claim and is recorded as one.
 */
const sentRevision = z.strictObject({
  decisionId: id,
  draftRevision: revision,
  actorId: id,
  reportedAt: instant,
  conversationRevision: revision,
  configRevision: revision,
});
export type SentRevision = z.infer<typeof sentRevision>;

const MeetingGoalCompletionSchema = z
  .strictObject({
    evidenceId: id,
    kind: z.literal("meeting"),
    source: z.literal("hubspot_readonly"),
    recipient: address,
    offeredAt: instant,
    meetingCreatedAt: instant,
    startsAt: instant,
    endsAt: instant,
    observedAt: instant,
    conversationRevision: revision,
    configRevision: revision,
  })
  .superRefine((completion, context) => {
    if (Date.parse(completion.startsAt) <= Date.parse(completion.offeredAt))
      context.addIssue({ code: "custom", path: ["startsAt"], message: "Meeting precedes offer" });
    if (Date.parse(completion.endsAt) <= Date.parse(completion.startsAt))
      context.addIssue({ code: "custom", path: ["endsAt"], message: "Meeting has no duration" });
    if (Date.parse(completion.meetingCreatedAt) > Date.parse(completion.observedAt))
      context.addIssue({
        code: "custom",
        path: ["observedAt"],
        message: "Meeting evidence was observed before creation",
      });
  });
export type MeetingGoalCompletion = z.infer<typeof MeetingGoalCompletionSchema>;
/**
 * The rep's one-tap "Mark booked" (inbox direction A, decision 2): a claim, not a receipt. It is
 * enough to stop proposing new times and never enough to state meeting details, so it carries who
 * and when and nothing about the meeting. `evidenceId` is the command that made it. For an event
 * campaign the same action records the registration the goal asks for.
 */
const RepGoalClaimSchema = z.strictObject({
  evidenceId: id,
  kind: z.enum(["meeting", "event_registration"]),
  source: z.literal("rep_claim"),
  actorId: id,
  claimedAt: instant,
  conversationRevision: revision,
  configRevision: revision,
});
type RepGoalClaim = z.infer<typeof RepGoalClaimSchema>;
const GoalCompletionSchema = z.discriminatedUnion("source", [
  MeetingGoalCompletionSchema,
  RepGoalClaimSchema,
]);
export type GoalCompletion = z.infer<typeof GoalCompletionSchema>;
/** A rep undoing a mistaken Mark booked. The booking stays in history; this names it withdrawn. */
const goalCorrection = z.strictObject({
  decisionId: id,
  supersedes: id,
  actorId: id,
  at: instant,
});
export type GoalCorrection = z.infer<typeof goalCorrection>;
/** When the goal was met: the booking's creation for a receipt, the tap for a rep's claim. */
export const goalCompletedAt = (completion: GoalCompletion): string =>
  completion.source === "rep_claim" ? completion.claimedAt : completion.meetingCreatedAt;
const dispositionSchema = z.enum([
  "active",
  "qualification_pending",
  "qualification_error",
  "ignored_not_qualified",
  "drafting_off",
  "back_drafting_disabled",
  "backfill_not_drafted",
  "sender_only_cc",
  "answered_by_seller",
  "meeting_already_confirmed",
  /**
   * The lead, or someone they brought in, wrote again after the seller answered by hand, and Bison
   * filed it with no lead or campaign: shown in the conversation, never drafted (LEA-315,
   * docs/2026-10-27-thread-continuation-plan.md).
   */
  "continued_not_drafted",
]);
const draftingHeldBySchema = z.enum(["workspace", "campaign", "lead"]);
export const WorkPlanSchema = z
  .strictObject({
    schemaVersion: z.literal(1),
    id,
    accountId: id,
    workspaceId: id,
    campaignId: id,
    conversationId: id,
    runId: id.nullable().default(null),
    // The campaign's own list, not a copy of it: the two cannot drift apart.
    goalKind: campaignGoalKindSchema.nullable().default(null),
    disposition: dispositionSchema.default("active"),
    /** Current inbound turn's host decision; older plans retain their prior inbox behavior. */
    turnTreatment: z.enum(["normal", "close_decline", "answer_then_close"]).default("normal"),
    /**
     * For `drafting_off`, the switch level that held it when intake decided (workspace, campaign or
     * lead), so the label can say where to turn drafting back on. Null for every other disposition
     * and for holds recorded before the switches existed.
     */
    draftingHeldBy: draftingHeldBySchema.nullable().default(null),
    operatorOutcome: operatorOutcome.nullable().default(null),
    operatorOutcomeHistory: z.array(operatorOutcomeDecision).default([]),
    goalCompletion: GoalCompletionSchema.nullable().default(null),
    goalCompletionHistory: z.array(GoalCompletionSchema).default([]),
    goalCompletionCorrections: z.array(goalCorrection).default([]),
    sentRevisions: z.array(sentRevision).default([]),
    followUpPromises: z.array(PlanFollowUpPromiseSchema).default([]),
    revision,
    conversationRevision: revision,
    configRevision: revision,
    steps: z.array(StepSchema),
    history: z.array(z.strictObject({ stepId: id, at: instant, evidence })),
  })
  .superRefine((plan, context) => {
    if (
      plan.operatorOutcome &&
      (plan.operatorOutcome.conversationRevision !== plan.conversationRevision ||
        plan.operatorOutcome.configRevision !== plan.configRevision)
    )
      context.addIssue({ code: "custom", message: "Operator outcome is stale" });
    if (plan.goalCompletion && plan.goalCompletion.configRevision !== plan.configRevision)
      context.addIssue({ code: "custom", message: "Goal completion is stale" });
    const ids = new Set(plan.steps.map((step) => step.id));
    if (ids.size !== plan.steps.length)
      context.addIssue({ code: "custom", message: "Duplicate step ID" });
    const visited = new Set<string>();
    const visiting = new Set<string>();
    const visit = (step: PlanStep): void => {
      if (visited.has(step.id)) return;
      if (visiting.has(step.id)) {
        context.addIssue({ code: "custom", message: "Cyclic dependencies" });
        return;
      }
      visiting.add(step.id);
      for (const dependency of step.dependencies) {
        const parent = plan.steps.find((candidate) => candidate.id === dependency);
        if (!parent) context.addIssue({ code: "custom", message: "Unknown dependency" });
        else visit(parent);
      }
      visiting.delete(step.id);
      visited.add(step.id);
    };
    for (const step of plan.steps) visit(step);
  });
export type WorkPlan = z.infer<typeof WorkPlanSchema>;
/**
 * Bookings still standing: the history less any a rep withdrew. This, not the history's length, is
 * what holds a later reply, so an undone Mark booked lets the next reply draft again.
 */
export function standingBookings(
  plan: Pick<WorkPlan, "goalCompletionHistory" | "goalCompletionCorrections">,
): GoalCompletion[] {
  const withdrawn = new Set(plan.goalCompletionCorrections.map((entry) => entry.supersedes));
  return plan.goalCompletionHistory.filter((entry) => !withdrawn.has(entry.evidenceId));
}

/** Why a visible conversation has no draft underway: every disposition except `active`. */
export type HeldDisposition = Exclude<WorkPlan["disposition"], "active">;

export class PlanConflict extends Error {
  constructor(
    readonly code:
      | "stale_revision"
      | "not_ready"
      | "invalid_transition"
      | "already_answered"
      | "not_found"
      | "reconciliation_required"
      | "redraft_in_progress",
  ) {
    super(code);
    this.name = "PlanConflict";
  }
}

export function approvalIsCurrent(plan: WorkPlan, step: PlanStep): boolean {
  const approval = step.approval;
  return (
    approval !== null &&
    approval.stepRevision === step.revision &&
    approval.conversationRevision === plan.conversationRevision &&
    approval.configRevision === plan.configRevision &&
    approval.draftRevision === (step.input.kind === "reply" ? step.input.draftRevision : null)
  );
}

/** Who a sent revision names when the app sent it on approval, not a rep by hand. */
export const APP_SEND_ACTOR_ID = "system:send-on-approve";

/**
 * True when the rep has reported mail sent for the conversation as it currently stands. Keyed on the
 * conversation revision the send was made against, so a new inbound reply lifts it and opens work
 * again while the revision that was actually sent stays closed.
 */
export function replySentForConversation(
  plan: Pick<WorkPlan, "sentRevisions">,
  conversationRevision: number,
): boolean {
  return plan.sentRevisions.some((entry) => entry.conversationRevision === conversationRevision);
}

export function dependenciesSatisfied(plan: WorkPlan, step: PlanStep): boolean {
  return step.dependencies.every((id) =>
    plan.steps.some(
      (parent) =>
        parent.id === id &&
        (parent.state === "completed" || (parent.state === "skipped" && !parent.mandatory)),
    ),
  );
}

/** Host-only proof of what an app-send operation was authorized to deliver. */
export const ReplyEffectBasisSchema = z.strictObject({
  operationId: id,
  stepId: id,
  stepRevision: revision,
  draftRevision: revision,
  conversationRevision: revision,
  configRevision: revision,
  readyPromises: z
    .array(PlanFollowUpPromiseSchema)
    .refine((promises) => promises.every((promise) => promise.state === "answer_ready")),
  readyPromiseAnswers: z
    .array(
      z.strictObject({
        promiseId: id,
        bindingReceiptId: z.uuid(),
        answerRevision: z.number().int().positive(),
        answerCommandId: id,
      }),
    )
    .exactOptional(),
});
export type ReplyEffectBasis = z.infer<typeof ReplyEffectBasisSchema>;

export type PlanCommand =
  | { type: "resolve_promise"; promiseId: string; at: string }
  | { type: "approve"; stepId: string; actorId: string; at: string }
  | { type: "queue"; stepId: string; operationId: string }
  | { type: "start"; stepId: string; operationId: string }
  | { type: "uncertain"; stepId: string; operationId: string }
  | {
      type: "complete";
      stepId: string;
      evidence: StepEvidence;
      at: string;
      omitLength?: boolean;
      effectBasis?: ReplyEffectBasis;
      /** Canonical host supplies exact, origin-verified obligations. Omission preserves V5 behavior. */
      answeredPromiseIds?: readonly string[];
      deliveredPromiseIds?: readonly string[];
      /**
       * A person's "it went out" for an uncertain reply LeadCue stopped looking for: a `rep_claim`
       * that completes the uncertain step under its own operation, instead of an approved one.
       */
      settlesUncertain?: { operationId: string };
    }
  | { type: "skip"; stepId: string }
  | { type: "revise"; stepId: string; input: StepInput }
  | {
      type: "set_operator_outcome";
      outcome: "no_response" | "deck_only" | null;
      decisionId: string;
      actorId: string;
      at: string;
    }
  | { type: "confirm_meeting_goal"; completion: MeetingGoalCompletion }
  | { type: "mark_booked"; decisionId: string; actorId: string; at: string }
  | { type: "undo_booked"; decisionId: string; actorId: string; at: string }
  | { type: "refresh"; conversationRevision: number; configRevision: number }
  | {
      type: "record_promise";
      stepId: string;
      promise: AuthorizedFollowUpPromise;
      at: string;
      competingPromiseIds?: readonly string[];
    };

/** The route and command guard use the same context contract the resume worker will parse. */
export function invalidQuestionAnswerKey(
  step: PlanStep,
  values: Extract<StepEvidence, { kind: "answer" }>["values"],
  uploadIds: readonly string[],
  omitLength = false,
): string | null {
  if (step.input.kind !== "question") return step.id;
  for (const key of Object.keys(values))
    if (!step.input.questions.some((question) => question.key === key)) return key;
  for (const question of step.input.questions) {
    const value = values[question.key];
    if (
      omitLength &&
      question.key === "meeting_duration_minutes" &&
      step.input.questions.length === 1 &&
      Object.keys(values).length === 0 &&
      uploadIds.length === 0
    )
      return null;
    if (question.answerMode === "yes_no_check") continue;
    if (question.key === BOOKING_CONTEXT_KEY) {
      if (!isExplicitBookingAbsence(value)) return question.key;
      continue;
    }
    if (!isContextKey(question.key)) return question.key;
    const schema = contextValueSchemas[question.key];
    if (question.uploadAllowed && uploadIds.length > 0 && value === undefined) {
      if (!schema.safeParse("Uploaded answer").success) return question.key;
      continue;
    }
    if (!schema.safeParse(value).success) return question.key;
  }
  return null;
}

/** Pure policy. The authenticated repository owns scope, CAS, command deduplication and the outbox. */
/**
 * An executing step whose effect was refused before it could start, so nothing reached the
 * provider: its approval is withdrawn, as any invalidation does, and it waits for a new one instead
 * of reading as an unknown outcome (defect row 13). Only the outbox applies it, under the row lock
 * and only while the effect-start stamp is null (`PlanRepository.refuseOutbox`).
 */
export function refuseExecutingStep(plan: WorkPlan, stepId: string, operationId: string): WorkPlan {
  const step = plan.steps.find((candidate) => candidate.id === stepId);
  if (step?.state !== "executing" || step.operationId !== operationId)
    throw new PlanConflict("invalid_transition");
  return {
    ...plan,
    revision: plan.revision + 1,
    steps: plan.steps.map((candidate) =>
      candidate.id === stepId
        ? { ...candidate, state: "invalidated", approval: null, operationId: null }
        : candidate,
    ),
  };
}

/**
 * An uncertain reply a person settled as not sent, once LeadCue stopped looking for it
 * (`docs/2026-10-22-forward-release-decisions.md`, Change A): its approval is withdrawn and it waits
 * for a new one, as a refused effect does. Only the outbox applies it, under the row lock
 * (`PlanRepository.settleUncertainReply`).
 */
export function releaseUncertainReply(
  plan: WorkPlan,
  stepId: string,
  operationId: string,
): WorkPlan {
  const step = plan.steps.find((candidate) => candidate.id === stepId);
  if (
    step?.state !== "uncertain" ||
    step.input.kind !== "reply" ||
    step.operationId !== operationId
  )
    throw new PlanConflict("invalid_transition");
  return {
    ...plan,
    revision: plan.revision + 1,
    steps: plan.steps.map((candidate) =>
      candidate.id === stepId
        ? { ...candidate, state: "invalidated", approval: null, operationId: null }
        : candidate,
    ),
  };
}

/**
 * A completion of the operation in flight: its own provider receipt, or a person settling it once
 * LeadCue stopped looking. Either carries the send's frozen basis and skips the dependency check,
 * which held when the send started.
 */
function completesInFlight(
  step: PlanStep,
  proof: StepEvidence,
  settles: { operationId: string } | undefined,
): boolean {
  if (settles)
    return (
      proof.kind === "rep_claim" &&
      step.state === "uncertain" &&
      step.operationId === settles.operationId
    );
  return (
    proof.kind === "provider_receipt" &&
    ["executing", "uncertain"].includes(step.state) &&
    proof.operationId === step.operationId
  );
}

/** Only a rep's claim may settle an uncertain reply; any other evidence naming it is refused. */
function refuseSettlementWithout(
  proof: StepEvidence,
  settles: { operationId: string } | undefined,
): void {
  if (settles && proof.kind !== "rep_claim") throw new PlanConflict("invalid_transition");
}

/**
 * Who may claim a step done by hand: manual, handoff and reply steps. A reply is claimed sent while
 * approved under its current approval, or, settling an uncertain send, under that send's operation.
 */
function assertRepClaim(
  plan: WorkPlan,
  step: PlanStep,
  settles: { operationId: string } | undefined,
): void {
  if (!["manual", "handoff", "reply"].includes(step.input.kind))
    throw new PlanConflict("invalid_transition");
  if (settles) {
    if (
      step.input.kind !== "reply" ||
      step.state !== "uncertain" ||
      step.operationId !== settles.operationId
    )
      throw new PlanConflict("invalid_transition");
    return;
  }
  if (step.input.kind === "reply" && (!approvalIsCurrent(plan, step) || step.state !== "approved"))
    throw new PlanConflict("not_ready");
}

export function applyPlanCommand(
  plan: WorkPlan,
  expectedRevision: number,
  command: PlanCommand,
): WorkPlan {
  if (
    command.type === "complete" &&
    command.evidence.kind === "answer" &&
    plan.steps.some((step) => step.id === command.stepId && step.state === "completed")
  )
    throw new PlanConflict("already_answered");
  if (plan.revision !== expectedRevision) throw new PlanConflict("stale_revision");
  if (command.type === "refresh") {
    if (
      command.conversationRevision < plan.conversationRevision ||
      command.configRevision < plan.configRevision
    )
      throw new PlanConflict("stale_revision");
    if (
      command.conversationRevision === plan.conversationRevision &&
      command.configRevision === plan.configRevision
    )
      return plan;
    return {
      ...plan,
      revision: plan.revision + 1,
      conversationRevision: command.conversationRevision,
      configRevision: command.configRevision,
      operatorOutcome:
        command.conversationRevision > plan.conversationRevision ||
        command.configRevision > plan.configRevision
          ? null
          : plan.operatorOutcome,
      goalCompletion: command.configRevision > plan.configRevision ? null : plan.goalCompletion,
      steps: plan.steps.map((step) =>
        refreshStep(
          step,
          command.conversationRevision === plan.conversationRevision,
          plan.conversationRevision,
        ),
      ),
    };
  }
  if (command.type === "set_operator_outcome") {
    if (plan.disposition !== "active") throw new PlanConflict("invalid_transition");
    if (plan.goalCompletion) throw new PlanConflict("invalid_transition");
    if (plan.steps.some((step) => ["executing", "uncertain"].includes(step.state)))
      throw new PlanConflict("reconciliation_required");
    if (command.outcome === null) {
      if (plan.operatorOutcome === null) throw new PlanConflict("invalid_transition");
      return {
        ...plan,
        revision: plan.revision + 1,
        operatorOutcome: null,
        operatorOutcomeHistory: [
          ...plan.operatorOutcomeHistory,
          {
            decisionId: command.decisionId,
            kind: "active",
            actorId: command.actorId,
            reviewedAt: command.at,
            conversationRevision: plan.conversationRevision,
            configRevision: plan.configRevision,
          },
        ],
        steps: plan.steps.map((step) =>
          step.state === "invalidated"
            ? { ...step, state: "pending", approval: null, operationId: null }
            : step,
        ),
      };
    }
    return {
      ...plan,
      revision: plan.revision + 1,
      operatorOutcome: {
        decisionId: command.decisionId,
        kind: command.outcome,
        actorId: command.actorId,
        reviewedAt: command.at,
        conversationRevision: plan.conversationRevision,
        configRevision: plan.configRevision,
      },
      operatorOutcomeHistory: [
        ...plan.operatorOutcomeHistory,
        {
          decisionId: command.decisionId,
          kind: command.outcome,
          actorId: command.actorId,
          reviewedAt: command.at,
          conversationRevision: plan.conversationRevision,
          configRevision: plan.configRevision,
        },
      ],
      steps: plan.steps.map((step) =>
        ["completed", "reported", "skipped"].includes(step.state)
          ? step
          : { ...step, state: "invalidated", approval: null, operationId: null },
      ),
    };
  }
  if (command.type === "confirm_meeting_goal") {
    if (
      plan.disposition !== "active" ||
      plan.operatorOutcome ||
      plan.goalKind !== "meeting" ||
      command.completion.conversationRevision !== plan.conversationRevision ||
      command.completion.configRevision !== plan.configRevision
    )
      throw new PlanConflict("invalid_transition");
    if (plan.steps.some((step) => ["executing", "uncertain"].includes(step.state)))
      throw new PlanConflict("reconciliation_required");
    if (plan.goalCompletion) {
      if (plan.goalCompletion.evidenceId === command.completion.evidenceId) return plan;
      throw new PlanConflict("invalid_transition");
    }
    return {
      ...plan,
      revision: plan.revision + 1,
      goalCompletion: command.completion,
      goalCompletionHistory: [...plan.goalCompletionHistory, command.completion],
      steps: plan.steps.map((step) =>
        ["completed", "reported", "skipped"].includes(step.state)
          ? step
          : { ...step, state: "invalidated", approval: null, operationId: null },
      ),
    };
  }
  if (command.type === "mark_booked") {
    if (plan.goalCompletion?.evidenceId === command.decisionId) return plan;
    if (
      plan.goalCompletion ||
      plan.operatorOutcome ||
      (plan.goalKind !== "meeting" && plan.goalKind !== "event_participation") ||
      plan.disposition === "ignored_not_qualified" ||
      plan.disposition === "sender_only_cc"
    )
      throw new PlanConflict("invalid_transition");
    if (plan.steps.some((step) => ["executing", "uncertain"].includes(step.state)))
      throw new PlanConflict("reconciliation_required");
    const claim: RepGoalClaim = {
      evidenceId: command.decisionId,
      kind: plan.goalKind === "meeting" ? "meeting" : "event_registration",
      source: "rep_claim",
      actorId: command.actorId,
      claimedAt: command.at,
      conversationRevision: plan.conversationRevision,
      configRevision: plan.configRevision,
    };
    return {
      ...plan,
      revision: plan.revision + 1,
      goalCompletion: claim,
      goalCompletionHistory: [...plan.goalCompletionHistory, claim],
      steps: plan.steps.map((step) =>
        ["completed", "reported", "skipped"].includes(step.state)
          ? step
          : { ...step, state: "invalidated", approval: null, operationId: null },
      ),
    };
  }
  if (command.type === "undo_booked") {
    if (plan.goalCompletionCorrections.some((entry) => entry.decisionId === command.decisionId))
      return plan;
    // Only a rep's own claim can be withdrawn by a rep; a provider receipt is evidence.
    const claim = plan.goalCompletion;
    if (claim?.source !== "rep_claim") throw new PlanConflict("invalid_transition");
    return {
      ...plan,
      revision: plan.revision + 1,
      goalCompletion: null,
      goalCompletionCorrections: [
        ...plan.goalCompletionCorrections,
        {
          decisionId: command.decisionId,
          supersedes: claim.evidenceId,
          actorId: command.actorId,
          at: command.at,
        },
      ],
      steps: plan.steps.map((step) =>
        step.state === "invalidated"
          ? { ...step, state: "pending", approval: null, operationId: null }
          : step,
      ),
    };
  }
  if (plan.goalCompletion) throw new PlanConflict("invalid_transition");
  if (command.type === "resolve_promise") {
    const held = plan.followUpPromises.find((promise) => promise.id === command.promiseId);
    if (!held || !["open", "answer_ready"].includes(held.state))
      throw new PlanConflict("invalid_transition");
    return {
      ...plan,
      revision: plan.revision + 1,
      steps: plan.steps.map((step) =>
        step.input.kind === "reply" || step.input.kind === "connector" ? invalidate(step) : step,
      ),
      followUpPromises: plan.followUpPromises.map((promise) =>
        promise.id === held.id
          ? { ...promise, state: "answer_ready", answeredAt: command.at }
          : promise,
      ),
    };
  }
  const step = plan.steps.find((candidate) => candidate.id === command.stepId);
  if (!step) throw new PlanConflict("not_found");
  if (command.type === "record_promise") {
    const question =
      step.input.kind === "question"
        ? step.input.questions.find(
            (candidate) =>
              candidate.key === command.promise.questionKey &&
              candidate.speechActId === command.promise.speechActId,
          )
        : null;
    if (
      question?.answerMode !== "yes_no_check" ||
      question.prompt !== command.promise.question ||
      // An invalidated question was only relabelled by an older refresh; it still awaits its answer.
      !["pending", "invalidated"].includes(step.state) ||
      command.promise.authorizedAt !== command.at ||
      plan.followUpPromises.some(
        (promise) =>
          ["open", "answer_ready"].includes(promise.state) &&
          (command.competingPromiseIds === undefined ||
            command.competingPromiseIds.includes(promise.id)),
      )
    )
      throw new PlanConflict("invalid_transition");
    const { status: _contextStatus, ...authorizedPromise } = command.promise;
    return {
      ...plan,
      revision: plan.revision + 1,
      followUpPromises: [
        ...plan.followUpPromises,
        {
          ...authorizedPromise,
          state: "open",
          answeredAt: null,
          keptAt: null,
        },
      ],
    };
  }
  let changed: PlanStep;
  let history = plan.history;
  let sentRevisions = plan.sentRevisions;
  let followUpPromises = plan.followUpPromises;
  // biome-ignore lint/nursery/useExhaustiveSwitchCases: non-step commands return above.
  switch (command.type) {
    case "approve": {
      if (!["pending", "invalidated", "approved"].includes(step.state))
        throw new PlanConflict("invalid_transition");
      if (!dependenciesSatisfied(plan, step)) throw new PlanConflict("not_ready");
      if (step.input.kind !== "reply" && step.input.kind !== "connector")
        throw new PlanConflict("invalid_transition");
      changed = {
        ...step,
        state: "approved",
        approval: {
          actorId: command.actorId,
          at: command.at,
          stepRevision: step.revision,
          conversationRevision: plan.conversationRevision,
          configRevision: plan.configRevision,
          draftRevision: step.input.kind === "reply" ? step.input.draftRevision : null,
        },
      };
      break;
    }
    case "queue":
      if (
        step.state !== "approved" ||
        !approvalIsCurrent(plan, step) ||
        !dependenciesSatisfied(plan, step)
      )
        throw new PlanConflict("not_ready");
      // A reply is queued only for send-on-approve; the repository refuses the queue unless the
      // workspace is granted it, so a reply reaches the outbox only when sending is selected.
      if (step.input.kind !== "connector" && step.input.kind !== "reply")
        throw new PlanConflict("invalid_transition");
      changed = { ...step, state: "queued", operationId: command.operationId };
      break;
    case "start":
      if (
        step.state !== "queued" ||
        step.operationId !== command.operationId ||
        !approvalIsCurrent(plan, step) ||
        !dependenciesSatisfied(plan, step)
      )
        throw new PlanConflict("not_ready");
      changed = { ...step, state: "executing" };
      break;
    case "uncertain":
      if (step.state !== "executing" || step.operationId !== command.operationId)
        throw new PlanConflict("invalid_transition");
      changed = { ...step, state: "uncertain" };
      break;
    case "skip":
      if (step.mandatory || !["pending", "invalidated"].includes(step.state))
        throw new PlanConflict("invalid_transition");
      changed = { ...step, state: "skipped" };
      break;
    case "revise":
      if (["executing", "uncertain"].includes(step.state))
        throw new PlanConflict("reconciliation_required");
      if (
        ["completed", "reported", "skipped"].includes(step.state) ||
        command.input.kind !== step.input.kind
      )
        throw new PlanConflict("invalid_transition");
      changed = {
        ...step,
        input: command.input,
        revision: step.revision + 1,
        state: "pending",
        approval: null,
        operationId: null,
      };
      break;
    case "complete": {
      const proof = command.evidence;
      refuseSettlementWithout(proof, command.settlesUncertain);
      const inFlightReceipt = completesInFlight(step, proof, command.settlesUncertain);
      if (!inFlightReceipt && !dependenciesSatisfied(plan, step))
        throw new PlanConflict("not_ready");
      const basis =
        command.effectBasis === undefined
          ? null
          : ReplyEffectBasisSchema.parse(command.effectBasis);
      if (
        basis !== null &&
        (!inFlightReceipt ||
          step.input.kind !== "reply" ||
          basis.operationId !== step.operationId ||
          basis.stepId !== step.id ||
          basis.stepRevision !== step.revision ||
          basis.draftRevision !== step.input.draftRevision ||
          basis.conversationRevision > plan.conversationRevision ||
          basis.configRevision > plan.configRevision)
      )
        throw new PlanConflict("invalid_transition");
      if (inFlightReceipt && step.input.kind === "reply" && basis === null)
        throw new PlanConflict("invalid_transition");
      if (["completed", "skipped"].includes(step.state))
        throw new PlanConflict("invalid_transition");
      if (proof.kind === "provider_receipt") {
        if (step.input.kind !== "connector" && step.input.kind !== "reply")
          throw new PlanConflict("invalid_transition");
        if (step.input.kind === "connector") {
          // A connector effect completes only while in flight under its own operation.
          if (
            !["executing", "uncertain"].includes(step.state) ||
            proof.operationId !== step.operationId
          )
            throw new PlanConflict("invalid_transition");
        } else if (["executing", "uncertain"].includes(step.state)) {
          // A reply the app is sending (send-on-approve) completes only under the operation that
          // started it, so a stale or foreign receipt can never confirm an in-flight send.
          if (proof.operationId !== step.operationId) throw new PlanConflict("invalid_transition");
        }
        // A reply not in flight is a passive observation of a manually sent reply; it still
        // completes, preserving the report-then-observe path from before the app could send.
      } else if (proof.kind === "rep_claim") {
        assertRepClaim(plan, step, command.settlesUncertain);
      } else if (proof.kind === "answer") {
        if (
          step.input.kind !== "question" ||
          (proof.uploadIds.length > 0 && step.input.questions.length !== 1) ||
          !step.input.questions.every((question) => {
            const value = proof.values[question.key];
            if (question.answerMode === "yes_no_check") {
              const response = RepQuestionResponseSchema.safeParse(value);
              return (
                response.success &&
                response.data.kind !== "need_to_check" &&
                response.data.questionKey === question.key &&
                response.data.question === question.prompt &&
                response.data.speechActId === question.speechActId
              );
            }
            /*
             * The booking question takes a record, not prose: `answerRun` parses its value with the
             * booking schema, so typed text could only ever fail in the worker, where the rep never
             * sees it. The app offers the one record a rep can give, the explicit absence.
             */
            if (question.key === BOOKING_CONTEXT_KEY) return isExplicitBookingAbsence(value);
            return (
              invalidQuestionAnswerKey(step, proof.values, proof.uploadIds, command.omitLength) ===
              null
            );
          })
        )
          throw new PlanConflict("not_ready");
      } else if (
        step.input.kind !== "wait" ||
        Date.parse(proof.observedAt) < Date.parse(step.input.until)
      )
        throw new PlanConflict("not_ready");
      changed = {
        ...step,
        state: proof.kind === "rep_claim" && step.input.kind === "reply" ? "reported" : "completed",
      };
      // A reply sent, whether the rep sent it by hand (rep_claim) or the app sent it on approval
      // (provider_receipt), records the exact revision now in the lead's mailbox. This, not the step
      // state, is what stops drafting proposing a replacement, because a regenerated draft rebuilds
      // the step under the same identifier.
      const replySent =
        step.input.kind === "reply" &&
        (proof.kind === "rep_claim" || proof.kind === "provider_receipt");
      if (replySent && step.input.kind === "reply") {
        const draftRevision = step.input.draftRevision;
        if (!sentRevisions.some((entry) => entry.draftRevision === draftRevision))
          sentRevisions = [
            ...sentRevisions,
            {
              decisionId: `${step.id}:sent-r${draftRevision}`,
              draftRevision,
              actorId: proof.kind === "rep_claim" ? proof.actorId : APP_SEND_ACTOR_ID,
              reportedAt: command.at,
              conversationRevision: basis?.conversationRevision ?? plan.conversationRevision,
              configRevision: basis?.configRevision ?? plan.configRevision,
            },
          ];
      }
      if (proof.kind === "answer") {
        const answered = new Set(
          Object.values(proof.values).flatMap((value) => {
            const response = RepQuestionResponseSchema.safeParse(value);
            return response.success && response.data.kind !== "need_to_check"
              ? [response.data.speechActId]
              : [];
          }),
        );
        followUpPromises = followUpPromises.map((promise) =>
          promise.state === "open" &&
          answered.has(promise.speechActId) &&
          (command.answeredPromiseIds === undefined ||
            command.answeredPromiseIds.includes(promise.id))
            ? { ...promise, state: "answer_ready" as const, answeredAt: command.at }
            : promise,
        );
      } else if (replySent) {
        followUpPromises = followUpPromises.map((promise) =>
          promise.state === "answer_ready" &&
          (basis === null ||
            basis.readyPromises.some((ready) => canonical(ready) === canonical(promise))) &&
          (command.deliveredPromiseIds === undefined ||
            command.deliveredPromiseIds.includes(promise.id))
            ? { ...promise, state: "kept" as const, keptAt: command.at }
            : promise,
        );
      }
      history = [...history, { stepId: step.id, at: command.at, evidence: proof }];
      break;
    }
  }
  const affected = new Set([step.id]);
  if (command.type === "revise") {
    let expanded = true;
    while (expanded) {
      expanded = false;
      for (const candidate of plan.steps)
        if (!affected.has(candidate.id) && candidate.dependencies.some((id) => affected.has(id))) {
          affected.add(candidate.id);
          expanded = true;
        }
    }
    if (
      plan.steps.some(
        (candidate) =>
          candidate.id !== step.id &&
          affected.has(candidate.id) &&
          ["executing", "uncertain"].includes(candidate.state),
      )
    )
      throw new PlanConflict("reconciliation_required");
  }
  return {
    ...plan,
    revision: plan.revision + 1,
    history,
    sentRevisions,
    followUpPromises,
    steps: plan.steps.map((candidate) =>
      candidate.id === step.id
        ? changed
        : affected.has(candidate.id)
          ? invalidate(candidate)
          : candidate,
    ),
  };
}

/**
 * What recording a seller reply closed, kept beside the record so a withdrawn report restores
 * exactly this and nothing else (R11 in docs/2026-09-23-compose-view-shape.md).
 */
export const TurnSettlementSchema = z.strictObject({
  steps: z.array(
    z.strictObject({
      stepId: id,
      priorState: StepSchema.shape.state,
      priorApproval: ApprovalSchema.nullable(),
    }),
  ),
  priorDisposition: dispositionSchema,
  priorDraftingHeldBy: draftingHeldBySchema.nullable(),
  keptPromiseIds: z.array(id),
  priorOperatorOutcome: operatorOutcome.nullable(),
});
export type TurnSettlement = z.infer<typeof TurnSettlementSchema>;

/**
 * Whether a seller reply closes this step (the state table): a question still waiting and the
 * pipeline's unsent draft. Shared with the compose box, which folds exactly these while the rep
 * writes, so what the page says will close is what the record closes.
 */
export function closesWithTurn(step: PlanStep): boolean {
  return (
    (step.input.kind === "question" && ["pending", "invalidated"].includes(step.state)) ||
    (step.input.kind === "reply" && ["pending", "invalidated", "approved"].includes(step.state)) ||
    // A reply outside the campaign's goal is the rep's to answer, so their reply settles it.
    (outsideGoalStepKind(step) !== null && ["pending", "invalidated"].includes(step.state)) ||
    // A lead-link task is the rep's to finish; their reply, "Send our link" or their own, settles it.
    (leadLinkStepPlacement(step) !== null && ["pending", "invalidated"].includes(step.state)) ||
    // So is a request for material: the rep's own reply answers it.
    (isMaterialStep(step) && ["pending", "invalidated"].includes(step.state)) ||
    // And a conditional no: the rep's own reply is their answer to whether it holds.
    (isConditionalNoStep(step) && ["pending", "invalidated"].includes(step.state))
  );
}

/**
 * A seller reply closes the lead's current turn (the compose view shape, "State table").
 *
 * Questions still waiting and the pipeline's unsent draft move to `skipped`, closed rather than
 * deleted; an answered question, a stop request, a chore beside the reply and anything already
 * settled stay as they are. An app send in flight refuses the whole record, because two replies to
 * one message is the failure this guards against; an uncertain one stays for a human to reconcile.
 * The disposition becomes `answered_by_seller`, a value every build already reads. A ready answer
 * counts as delivered, and a reviewed outcome ends because the seller replied anyway (D5). Only
 * values the plan already has are written, so a rollback build parses the result.
 */
export function settleTurn(
  plan: WorkPlan,
  at: string,
  eligiblePromiseIds?: readonly string[],
): { plan: WorkPlan; settlement: TurnSettlement } {
  if (
    plan.steps.some(
      (step) => step.input.kind === "reply" && ["queued", "executing"].includes(step.state),
    )
  )
    throw new PlanConflict("reconciliation_required");
  const settled: TurnSettlement["steps"] = [];
  const steps = plan.steps.map((step): PlanStep => {
    if (!closesWithTurn(step)) return step;
    settled.push({ stepId: step.id, priorState: step.state, priorApproval: step.approval });
    return { ...step, state: "skipped", approval: null, operationId: null };
  });
  const keptPromiseIds = plan.followUpPromises
    .filter(
      (promise) =>
        promise.state === "answer_ready" &&
        (eligiblePromiseIds === undefined || eligiblePromiseIds.includes(promise.id)),
    )
    .map((promise) => promise.id);
  return {
    plan: {
      ...plan,
      revision: plan.revision + 1,
      disposition: "answered_by_seller",
      draftingHeldBy: null,
      operatorOutcome: null,
      followUpPromises: plan.followUpPromises.map((promise) =>
        keptPromiseIds.includes(promise.id)
          ? { ...promise, state: "kept" as const, keptAt: at }
          : promise,
      ),
      steps,
    },
    settlement: {
      steps: settled,
      priorDisposition: plan.disposition,
      priorDraftingHeldBy: plan.draftingHeldBy,
      keptPromiseIds,
      priorOperatorOutcome: plan.operatorOutcome,
    },
  };
}

/**
 * Undo one settlement: the steps it skipped return to where they stood, if nothing has moved them
 * since, and so do the disposition, the promises it counted as kept and a reviewed outcome still
 * bound to this conversation. Steps it did not settle are untouched, so a step invalidated for some
 * other reason stays invalidated. This is not `undo_booked`'s revive-every-invalidated-step.
 */
export function restoreTurn(plan: WorkPlan, settlement: TurnSettlement): WorkPlan {
  const byStep = new Map(settlement.steps.map((entry) => [entry.stepId, entry]));
  const kept = new Set(settlement.keptPromiseIds);
  const outcome = settlement.priorOperatorOutcome;
  return {
    ...plan,
    revision: plan.revision + 1,
    disposition: settlement.priorDisposition,
    draftingHeldBy: settlement.priorDraftingHeldBy,
    operatorOutcome:
      outcome &&
      outcome.conversationRevision === plan.conversationRevision &&
      outcome.configRevision === plan.configRevision
        ? outcome
        : null,
    followUpPromises: plan.followUpPromises.map((promise) =>
      kept.has(promise.id) && promise.state === "kept"
        ? { ...promise, state: "answer_ready" as const, keptAt: null }
        : promise,
    ),
    steps: plan.steps.map((step): PlanStep => {
      const entry = byStep.get(step.id);
      if (!entry || step.state !== "skipped") return step;
      return { ...step, state: entry.priorState, approval: entry.priorApproval };
    }),
  };
}

function invalidate(step: PlanStep): PlanStep {
  // In-flight effects retain their operation identity and must be reconciled, never silently retried.
  if (["executing", "uncertain"].includes(step.state))
    return { ...step, state: "uncertain", approval: null };
  if (["completed", "reported", "skipped"].includes(step.state)) return step;
  return { ...step, state: "invalidated", approval: null, operationId: null };
}

/*
 * A newer thread or configuration revokes what the rep approved or queued, and flags a pending reply
 * or connector for re-approval, since its content was built for the older revision. Unapproved rep
 * work with nothing to revoke (a question, a manual step, a handoff, a wait) stays pending: demoting
 * it only relabelled it, and it stranded "Need to check", which a question can carry only while
 * pending (configuration revision 3, 2026-09-19). A revised dependency still invalidates dependents
 * through `invalidate`, because there the step's own premise changed.
 */
const REAPPROVED = new Set<PlanStep["input"]["kind"]>(["reply", "connector"]);
function refreshStep(
  step: PlanStep,
  sameConversation: boolean,
  conversationRevision: number,
): PlanStep {
  // Legacy configuration applies invalidated unapproved questions even though the thread did not
  // move. The question is still the rep's next action; a newer thread must never revive it.
  if (
    sameConversation &&
    step.state === "invalidated" &&
    step.input.kind === "question" &&
    step.dependencies.length === 0 &&
    new RegExp(`-r${conversationRevision}-c[0-9]+$`).test(step.id) &&
    step.approval === null &&
    step.operationId === null
  )
    return { ...step, state: "pending" };
  if (step.state === "pending" && !REAPPROVED.has(step.input.kind)) return step;
  return invalidate(step);
}
